/* eslint-disable typebox-snake-case/typebox-object-keys-snake-case -- camelCase keys match existing DB/API schema */
import { Static, TOptional, TString, Type } from '@sinclair/typebox';
import { ComponentMeta } from '../website_types/BuilderTypes';
import { EMAIL_COMPONENT_IDS } from './EmailTypes';

// ─── Email Header ─────────────────────────────────────────────────────

export const EmailHeaderSchema = Type.Object({
  logoUrl: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_HEADER_LOGO',
      description: 'URL du logo',
      meta: { input_type: 'image' },
    }),
  ),
  // Absent = petit (48 px de haut) : rendu historique des emails, inchangé.
  logoSize: Type.Optional(
    Type.Union(
      [
        Type.Literal('small', { label: 'EMAIL_BUILDER_HEADER_LOGO_SIZE_SMALL' }),
        Type.Literal('medium', { label: 'EMAIL_BUILDER_HEADER_LOGO_SIZE_MEDIUM' }),
        Type.Literal('large', { label: 'EMAIL_BUILDER_HEADER_LOGO_SIZE_LARGE' }),
      ],
      {
        title: 'EMAIL_BUILDER_HEADER_LOGO_SIZE',
        default: 'small',
        meta: { multiple: false, disable_search: true },
      },
    ),
  ),
});

export type EmailHeaderData = Static<typeof EmailHeaderSchema>;
export type EmailHeaderLogoSize = NonNullable<EmailHeaderData['logoSize']>;

/** Hauteur max du logo (px) par taille — partagée entre le rendu et ses tests. */
export const EMAIL_HEADER_LOGO_MAX_HEIGHT: Record<EmailHeaderLogoSize, number> = {
  small: 48,
  medium: 88,
  large: 140,
};
export type EmailHeaderVariant = 'logo-left' | 'logo-center';

export const EmailHeaderMeta: ComponentMeta<
  typeof EmailHeaderSchema,
  EmailHeaderVariant,
  keyof EmailHeaderData & string
> = {
  id: EMAIL_COMPONENT_IDS.HEADER,
  version: '1.0.0',
  schema: EmailHeaderSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    'logo-left': {
      label: 'EMAIL_BUILDER_VARIANT_LOGO_LEFT',
      required: ['logoUrl'],
      optional: ['logoSize'],
      hidden: [],
      defaults: { logoUrl: '{{pro:logo}}' },
    },
    'logo-center': {
      label: 'EMAIL_BUILDER_VARIANT_LOGO_CENTER',
      required: ['logoUrl'],
      optional: ['logoSize'],
      hidden: [],
      defaults: { logoUrl: '{{pro:logo}}' },
    },
  },
};

// ─── Email Two Columns ───────────────────────────────────────────────

export const EmailTwoColumnsSchema = Type.Object({
  imageUrl: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_TWO_COLUMNS_IMAGE',
      meta: { input_type: 'image' },
    }),
  ),
  text: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_TWO_COLUMNS_TEXT',
      placeholder: 'EMAIL_BUILDER_TWO_COLUMNS_TEXT_PLACEHOLDER',
      meta: {
        input_type: 'rich_text',
        allowed_tags: ['h1', 'h2', 'b', 'i', 'u', 'a', 'ul', 'ol', 'variable', 'align'],
      },
    }),
  ),
});

export type EmailTwoColumnsData = Static<typeof EmailTwoColumnsSchema>;
export type EmailTwoColumnsVariant = 'image-left' | 'image-right';

export const EmailTwoColumnsMeta: ComponentMeta<
  typeof EmailTwoColumnsSchema,
  EmailTwoColumnsVariant,
  keyof EmailTwoColumnsData & string
> = {
  id: EMAIL_COMPONENT_IDS.TWO_COLUMNS,
  version: '1.0.0',
  schema: EmailTwoColumnsSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    'image-left': {
      label: 'EMAIL_BUILDER_VARIANT_IMAGE_LEFT',
      required: ['imageUrl'],
      optional: ['text'],
      hidden: [],
      defaults: {
        text: 'Présentez ici votre message en quelques phrases pour accompagner votre image.',
      },
    },
    'image-right': {
      label: 'EMAIL_BUILDER_VARIANT_IMAGE_RIGHT',
      required: ['imageUrl'],
      optional: ['text'],
      hidden: [],
      defaults: {
        text: 'Présentez ici votre message en quelques phrases pour accompagner votre image.',
      },
    },
  },
};

// ─── Email Text ───────────────────────────────────────────────────────

export const EmailTextSchema = Type.Object({
  content: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_TEXT_CONTENT',
      placeholder: 'EMAIL_BUILDER_TEXT_CONTENT_PLACEHOLDER',
      meta: {
        input_type: 'rich_text',
        allowed_tags: ['h1', 'h2', 'b', 'i', 'u', 'a', 'ul', 'ol', 'variable', 'align'],
      },
    }),
  ),
});

export type EmailTextData = Static<typeof EmailTextSchema>;
export type EmailTextVariant = 'default' | 'callout' | 'framed';

export const EmailTextMeta: ComponentMeta<typeof EmailTextSchema, EmailTextVariant, keyof EmailTextData & string> = {
  id: EMAIL_COMPONENT_IDS.TEXT,
  version: '1.0.0',
  schema: EmailTextSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    default: {
      label: 'EMAIL_BUILDER_VARIANT_DEFAULT',
      required: ['content'],
      optional: [],
      hidden: [],
      defaults: {
        content:
          'Cher(e) client(e),<br><br>Saisissez ici le contenu de votre message.<br><br>À bientôt !<br>{{pro:name}}',
      },
    },
    callout: {
      label: 'EMAIL_BUILDER_VARIANT_CALLOUT',
      required: ['content'],
      optional: [],
      hidden: [],
      defaults: {
        content: '<h1>Votre titre ici</h1>Décrivez ici votre offre ou votre message important.',
      },
    },
    // Cadre fin sur tout le pourtour (sans fond teinté) : l'encart « code + contenu + validité » de la
    // carte cadeau, à la manière d'un bon imprimé.
    framed: {
      label: 'EMAIL_BUILDER_VARIANT_FRAMED',
      required: ['content'],
      optional: [],
      hidden: [],
      defaults: {
        content:
          '<h2 style="text-align: center">Votre titre ici</h2><p style="text-align: center">Votre texte ici.</p>',
      },
    },
  },
};

// ─── Email Button ─────────────────────────────────────────────────────

export const EmailButtonSchema = Type.Object({
  label: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_BUTTON_LABEL',
      placeholder: 'EMAIL_BUILDER_BUTTON_LABEL_PLACEHOLDER',
    }),
  ),
  url: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_BUTTON_URL',
      placeholder: 'EMAIL_BUILDER_BUTTON_URL_PLACEHOLDER',
      meta: { field_type: 'link' },
    }),
  ),
});

export type EmailButtonData = Static<typeof EmailButtonSchema>;
export type EmailButtonVariant = 'left' | 'center' | 'right';

export const EmailButtonMeta: ComponentMeta<
  typeof EmailButtonSchema,
  EmailButtonVariant,
  keyof EmailButtonData & string
> = {
  id: EMAIL_COMPONENT_IDS.BUTTON,
  version: '1.0.0',
  schema: EmailButtonSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    left: {
      label: 'EMAIL_BUILDER_VARIANT_LEFT',
      required: ['label', 'url'],
      optional: [],
      hidden: [],
      defaults: { label: 'Prendre rendez-vous', url: '{{pro:booking}}' },
    },
    center: {
      label: 'EMAIL_BUILDER_VARIANT_CENTER',
      required: ['label', 'url'],
      optional: [],
      hidden: [],
      defaults: { label: 'Prendre rendez-vous', url: '{{pro:booking}}' },
    },
    right: {
      label: 'EMAIL_BUILDER_VARIANT_RIGHT',
      required: ['label', 'url'],
      optional: [],
      hidden: [],
      defaults: { label: 'Prendre rendez-vous', url: '{{pro:booking}}' },
    },
  },
};

// ─── Email Image ──────────────────────────────────────────────────────

export const EmailImageSchema = Type.Object({
  imageUrl: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_IMAGE_URL',
      meta: { input_type: 'image' },
    }),
  ),
});

export type EmailImageData = Static<typeof EmailImageSchema>;
export type EmailImageVariant = 'full-width' | 'centered';

export const EmailImageMeta: ComponentMeta<typeof EmailImageSchema, EmailImageVariant, keyof EmailImageData & string> =
  {
    id: EMAIL_COMPONENT_IDS.IMAGE,
    version: '1.0.0',
    schema: EmailImageSchema,
    movable: true,
    allowMultipleAdd: true,
    variants: {
      'full-width': {
        label: 'EMAIL_BUILDER_VARIANT_FULL_WIDTH',
        required: ['imageUrl'],
        optional: [],
        hidden: [],
      },
      centered: {
        label: 'EMAIL_BUILDER_VARIANT_CENTERED',
        required: ['imageUrl'],
        optional: [],
        hidden: [],
      },
    },
  };

// ─── Email Divider ────────────────────────────────────────────────────

export const EmailDividerSchema = Type.Object({
  color: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_DIVIDER_COLOR',
      meta: { input_type: 'color_picker' },
    }),
  ),
});

export type EmailDividerData = Static<typeof EmailDividerSchema>;
export type EmailDividerVariant = 'line' | 'space';

export const EmailDividerMeta: ComponentMeta<
  typeof EmailDividerSchema,
  EmailDividerVariant,
  keyof EmailDividerData & string
> = {
  id: EMAIL_COMPONENT_IDS.DIVIDER,
  version: '1.0.0',
  schema: EmailDividerSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    line: {
      label: 'EMAIL_BUILDER_VARIANT_LINE',
      required: [],
      optional: ['color'],
      hidden: [],
    },
    space: {
      label: 'EMAIL_BUILDER_VARIANT_SPACE',
      required: [],
      optional: [],
      hidden: ['color'],
    },
  },
};

// ─── Email Social ─────────────────────────────────────────────────────

export const EmailSocialSchema = Type.Object({});

export type EmailSocialData = Static<typeof EmailSocialSchema>;
export type EmailSocialVariant = 'simple' | 'filled';

export const EmailSocialMeta: ComponentMeta<
  typeof EmailSocialSchema,
  EmailSocialVariant,
  keyof EmailSocialData & string
> = {
  id: EMAIL_COMPONENT_IDS.SOCIAL,
  version: '1.0.0',
  schema: EmailSocialSchema,
  movable: true,
  allowMultipleAdd: false,
  variants: {
    simple: {
      label: 'EMAIL_BUILDER_VARIANT_SIMPLE',
      required: [],
      optional: [],
      hidden: [],
    },
    filled: {
      label: 'EMAIL_BUILDER_VARIANT_FILLED',
      required: [],
      optional: [],
      hidden: [],
    },
  },
};

// ─── Email Prestation ─────────────────────────────────────────────────

export const EmailPrestationSchema = Type.Object({
  prestationId: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_PRESTATION_ID',
      // link_field: when a prestation is picked, the CTA link is seeded with that
      // prestation's website page binding ({{prestation:<id>}}).
      meta: { input_type: 'prestation_compact', link_field: 'ctaUrl', link_binding: 'prestation' },
    }),
  ),
  showPrice: Type.Optional(
    Type.Boolean({
      title: 'EMAIL_BUILDER_PRESTATION_SHOW_PRICE',
      default: true,
    }),
  ),
  showDuration: Type.Optional(
    Type.Boolean({
      title: 'EMAIL_BUILDER_PRESTATION_SHOW_DURATION',
      default: true,
    }),
  ),
  ctaLabel: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_PRESTATION_CTA_LABEL',
    }),
  ),
  ctaUrl: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_PRESTATION_CTA_URL',
      meta: { field_type: 'link' },
    }),
  ),
});

export type EmailPrestationData = Static<typeof EmailPrestationSchema>;
export type EmailPrestationVariant = 'card' | 'inline';

export const EmailPrestationMeta: ComponentMeta<
  typeof EmailPrestationSchema,
  EmailPrestationVariant,
  keyof EmailPrestationData & string
> = {
  id: EMAIL_COMPONENT_IDS.PRESTATION,
  version: '1.0.0',
  schema: EmailPrestationSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    card: {
      label: 'EMAIL_BUILDER_VARIANT_CARD',
      required: [],
      optional: ['prestationId', 'showPrice', 'showDuration', 'ctaLabel', 'ctaUrl'],
      hidden: [],
      defaults: { showPrice: true, showDuration: true, ctaLabel: 'Réserver' },
    },
    inline: {
      label: 'EMAIL_BUILDER_VARIANT_INLINE',
      required: [],
      optional: ['prestationId', 'showPrice', 'showDuration', 'ctaLabel', 'ctaUrl'],
      hidden: [],
      defaults: { showPrice: true, showDuration: true, ctaLabel: 'Réserver' },
    },
  },
};

// ─── Email Footer ─────────────────────────────────────────────────────

export const EmailFooterSchema = Type.Object({
  companyName: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_FOOTER_COMPANY_NAME',
    }),
  ),
  address: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_FOOTER_ADDRESS',
    }),
  ),
  unsubscribeLabel: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_FOOTER_UNSUBSCRIBE_LABEL',
    }),
  ),
});

export type EmailFooterData = Static<typeof EmailFooterSchema>;
export type EmailFooterVariant = 'simple' | 'complet' | 'transactional';

export const EmailFooterMeta: ComponentMeta<
  typeof EmailFooterSchema,
  EmailFooterVariant,
  keyof EmailFooterData & string
> = {
  id: EMAIL_COMPONENT_IDS.FOOTER,
  version: '1.0.0',
  schema: EmailFooterSchema,
  movable: false,
  allowMultipleAdd: false,
  variants: {
    simple: {
      label: 'EMAIL_BUILDER_VARIANT_SIMPLE',
      required: ['unsubscribeLabel'],
      optional: [],
      hidden: ['companyName', 'address'],
      defaults: { unsubscribeLabel: 'Se désabonner' },
    },
    complet: {
      label: 'EMAIL_BUILDER_VARIANT_COMPLET',
      required: ['unsubscribeLabel'],
      optional: ['companyName', 'address'],
      hidden: [],
      defaults: {
        companyName: '{{pro:name}}',
        address: '{{pro:address:full}}',
        unsubscribeLabel: 'Se désabonner',
      },
    },
    transactional: {
      label: 'EMAIL_BUILDER_VARIANT_TRANSACTIONAL',
      required: [],
      optional: ['companyName', 'address'],
      hidden: ['unsubscribeLabel'],
      defaults: {
        companyName: '{{pro:name}}',
        address: '{{pro:address:full}}',
      },
    },
  },
};

// ═══ Blocs V2 ═══════════════════════════════════════════════════════════
// Quatre blocs simples, rétro-compatibles (aucun modèle de la bibliothèque n'en dépend), qui
// respectent les 4 couleurs du thème. Champs texte en texte BRUT (rendus et échappés par React),
// pas en texte riche : leur mise en forme (taille, couleur) est portée par le bloc lui-même.

// ─── Email Hero : titre sur image de fond ─────────────────────────────

export const EmailHeroSchema = Type.Object({
  imageUrl: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_HERO_IMAGE',
      meta: { input_type: 'image' },
    }),
  ),
  title: Type.Optional(
    Type.String({ title: 'EMAIL_BUILDER_HERO_TITLE', placeholder: 'EMAIL_BUILDER_HERO_TITLE_PLACEHOLDER' }),
  ),
  subtitle: Type.Optional(
    Type.String({ title: 'EMAIL_BUILDER_HERO_SUBTITLE', placeholder: 'EMAIL_BUILDER_HERO_SUBTITLE_PLACEHOLDER' }),
  ),
});

export type EmailHeroData = Static<typeof EmailHeroSchema>;
export type EmailHeroVariant = 'tall' | 'compact';

export const EmailHeroMeta: ComponentMeta<typeof EmailHeroSchema, EmailHeroVariant, keyof EmailHeroData & string> = {
  id: EMAIL_COMPONENT_IDS.HERO,
  version: '1.0.0',
  schema: EmailHeroSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    tall: {
      label: 'EMAIL_BUILDER_VARIANT_TALL',
      required: ['imageUrl', 'title'],
      optional: ['subtitle'],
      hidden: [],
      defaults: { title: 'Votre titre', subtitle: 'Une phrase pour accompagner le titre' },
    },
    compact: {
      label: 'EMAIL_BUILDER_VARIANT_COMPACT',
      required: ['imageUrl', 'title'],
      optional: ['subtitle'],
      hidden: [],
      defaults: { title: 'Votre titre' },
    },
  },
};

// ─── Email Three Columns : 3 vignettes côte à côte ────────────────────

const columnImage = (n: 1 | 2 | 3): TOptional<TString> =>
  Type.Optional(Type.String({ title: `EMAIL_BUILDER_COLUMN_${n}_IMAGE`, meta: { input_type: 'image' } }));
const columnTitle = (n: 1 | 2 | 3): TOptional<TString> =>
  Type.Optional(
    Type.String({ title: `EMAIL_BUILDER_COLUMN_${n}_TITLE`, placeholder: 'EMAIL_BUILDER_COLUMN_TITLE_PLACEHOLDER' }),
  );
const columnText = (n: 1 | 2 | 3): TOptional<TString> =>
  Type.Optional(
    Type.String({ title: `EMAIL_BUILDER_COLUMN_${n}_TEXT`, placeholder: 'EMAIL_BUILDER_COLUMN_TEXT_PLACEHOLDER' }),
  );

export const EmailThreeColumnsSchema = Type.Object({
  image1: columnImage(1),
  title1: columnTitle(1),
  text1: columnText(1),
  image2: columnImage(2),
  title2: columnTitle(2),
  text2: columnText(2),
  image3: columnImage(3),
  title3: columnTitle(3),
  text3: columnText(3),
});

export type EmailThreeColumnsData = Static<typeof EmailThreeColumnsSchema>;
export type EmailThreeColumnsVariant = 'images' | 'text-only';

const THREE_COLUMNS_DEFAULTS: Partial<EmailThreeColumnsData> = {
  title1: 'Étape 1',
  text1: 'Une ligne pour la décrire',
  title2: 'Étape 2',
  text2: 'Une ligne pour la décrire',
  title3: 'Étape 3',
  text3: 'Une ligne pour la décrire',
};

export const EmailThreeColumnsMeta: ComponentMeta<
  typeof EmailThreeColumnsSchema,
  EmailThreeColumnsVariant,
  keyof EmailThreeColumnsData & string
> = {
  id: EMAIL_COMPONENT_IDS.THREE_COLUMNS,
  version: '1.0.0',
  schema: EmailThreeColumnsSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    images: {
      label: 'EMAIL_BUILDER_VARIANT_IMAGES',
      required: [],
      optional: ['image1', 'title1', 'text1', 'image2', 'title2', 'text2', 'image3', 'title3', 'text3'],
      hidden: [],
      defaults: THREE_COLUMNS_DEFAULTS,
    },
    'text-only': {
      label: 'EMAIL_BUILDER_VARIANT_TEXT_ONLY',
      required: [],
      optional: ['title1', 'text1', 'title2', 'text2', 'title3', 'text3'],
      hidden: ['image1', 'image2', 'image3'],
      defaults: THREE_COLUMNS_DEFAULTS,
    },
  },
};

// ─── Email Stat : grand chiffre + légende ─────────────────────────────

const statValue = (n: 1 | 2 | 3): TOptional<TString> =>
  Type.Optional(
    Type.String({ title: `EMAIL_BUILDER_STAT_VALUE_${n}`, placeholder: 'EMAIL_BUILDER_STAT_VALUE_PLACEHOLDER' }),
  );
const statLabel = (n: 1 | 2 | 3): TOptional<TString> =>
  Type.Optional(
    Type.String({ title: `EMAIL_BUILDER_STAT_LABEL_${n}`, placeholder: 'EMAIL_BUILDER_STAT_LABEL_PLACEHOLDER' }),
  );

export const EmailStatSchema = Type.Object({
  value1: statValue(1),
  label1: statLabel(1),
  value2: statValue(2),
  label2: statLabel(2),
  value3: statValue(3),
  label3: statLabel(3),
});

export type EmailStatData = Static<typeof EmailStatSchema>;
export type EmailStatVariant = 'single' | 'row';

export const EmailStatMeta: ComponentMeta<typeof EmailStatSchema, EmailStatVariant, keyof EmailStatData & string> = {
  id: EMAIL_COMPONENT_IDS.STAT,
  version: '1.0.0',
  schema: EmailStatSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    single: {
      label: 'EMAIL_BUILDER_VARIANT_SINGLE',
      required: ['value1'],
      optional: ['label1'],
      hidden: ['value2', 'label2', 'value3', 'label3'],
      defaults: { value1: '-20 %', label1: 'sur votre prochain rendez-vous' },
    },
    row: {
      label: 'EMAIL_BUILDER_VARIANT_ROW',
      required: ['value1'],
      optional: ['label1', 'value2', 'label2', 'value3', 'label3'],
      hidden: [],
      defaults: {
        value1: '-20 %',
        label1: 'sur votre prochain rendez-vous',
        value2: '48 h',
        label2: 'pour en profiter',
        value3: '3',
        label3: 'étapes pour réserver',
      },
    },
  },
};

// ─── Email Deadline : date limite mise en avant ───────────────────────

export const EmailDeadlineSchema = Type.Object({
  label: Type.Optional(
    Type.String({ title: 'EMAIL_BUILDER_DEADLINE_LABEL', placeholder: 'EMAIL_BUILDER_DEADLINE_LABEL_PLACEHOLDER' }),
  ),
  date: Type.Optional(
    Type.String({ title: 'EMAIL_BUILDER_DEADLINE_DATE', placeholder: 'EMAIL_BUILDER_DEADLINE_DATE_PLACEHOLDER' }),
  ),
  note: Type.Optional(
    Type.String({ title: 'EMAIL_BUILDER_DEADLINE_NOTE', placeholder: 'EMAIL_BUILDER_DEADLINE_NOTE_PLACEHOLDER' }),
  ),
});

export type EmailDeadlineData = Static<typeof EmailDeadlineSchema>;
export type EmailDeadlineVariant = 'banner' | 'outline';

export const EmailDeadlineMeta: ComponentMeta<
  typeof EmailDeadlineSchema,
  EmailDeadlineVariant,
  keyof EmailDeadlineData & string
> = {
  id: EMAIL_COMPONENT_IDS.DEADLINE,
  version: '1.0.0',
  schema: EmailDeadlineSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    banner: {
      label: 'EMAIL_BUILDER_VARIANT_BANNER',
      required: ['date'],
      optional: ['label', 'note'],
      hidden: [],
      defaults: { label: "Jusqu'au", date: '31 décembre' },
    },
    outline: {
      label: 'EMAIL_BUILDER_VARIANT_OUTLINE',
      required: ['date'],
      optional: ['label', 'note'],
      hidden: [],
      defaults: { label: "Jusqu'au", date: '31 décembre' },
    },
  },
};

// ─── Email Promo Code : code de la promotion de la campagne + bouton ──
// Code en gros et bouton « Réserver » qui l'applique dans le tunnel (`?code=` ajouté au lien, cf.
// `appendPromoCodeToUrl`). Promotion automatique : `{{promo:code}}` vide, le bloc ne garde que le bouton,
// qui part sans `?code=`. Proposé à l'ajout dans l'éditeur de campagne seulement (`EMAIL_OPT_IN_COMPONENT_IDS`).

export const EmailPromoCodeSchema = Type.Object({
  title: Type.Optional(
    Type.String({ title: 'EMAIL_BUILDER_PROMO_CODE_TITLE', placeholder: 'EMAIL_BUILDER_PROMO_CODE_TITLE_PLACEHOLDER' }),
  ),
  code: Type.Optional(
    Type.String({ title: 'EMAIL_BUILDER_PROMO_CODE_CODE', placeholder: 'EMAIL_BUILDER_PROMO_CODE_CODE_PLACEHOLDER' }),
  ),
  note: Type.Optional(
    Type.String({ title: 'EMAIL_BUILDER_PROMO_CODE_NOTE', placeholder: 'EMAIL_BUILDER_PROMO_CODE_NOTE_PLACEHOLDER' }),
  ),
  ctaLabel: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_PROMO_CODE_CTA_LABEL',
      placeholder: 'EMAIL_BUILDER_PROMO_CODE_CTA_LABEL_PLACEHOLDER',
    }),
  ),
  ctaUrl: Type.Optional(
    Type.String({
      title: 'EMAIL_BUILDER_PROMO_CODE_CTA_URL',
      placeholder: 'EMAIL_BUILDER_BUTTON_URL_PLACEHOLDER',
      meta: { field_type: 'link' },
    }),
  ),
});

export type EmailPromoCodeData = Static<typeof EmailPromoCodeSchema>;
export type EmailPromoCodeVariant = 'framed' | 'filled';

const PROMO_CODE_DEFAULTS: Partial<EmailPromoCodeData> = {
  title: 'Votre code promo',
  code: '{{promo:code}}',
  ctaLabel: 'Réserver',
  ctaUrl: '{{pro:booking}}',
};

export const EmailPromoCodeMeta: ComponentMeta<
  typeof EmailPromoCodeSchema,
  EmailPromoCodeVariant,
  keyof EmailPromoCodeData & string
> = {
  id: EMAIL_COMPONENT_IDS.PROMO_CODE,
  version: '1.0.0',
  schema: EmailPromoCodeSchema,
  movable: true,
  allowMultipleAdd: true,
  variants: {
    framed: {
      label: 'EMAIL_BUILDER_VARIANT_FRAMED',
      required: [],
      optional: ['title', 'code', 'note', 'ctaLabel', 'ctaUrl'],
      hidden: [],
      defaults: PROMO_CODE_DEFAULTS,
    },
    filled: {
      label: 'EMAIL_BUILDER_VARIANT_FILLED',
      required: [],
      optional: ['title', 'code', 'note', 'ctaLabel', 'ctaUrl'],
      hidden: [],
      defaults: PROMO_CODE_DEFAULTS,
    },
  },
};
